import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, PageSubSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, KMCurve, ComparisonTable, TextBlock} from '../../components';
import { jobCodes } from '../../util/data/jobCodes';
import { createMarkup } from '../../util/functions';
import { Heading } from '../../util/typography';
import { StaticImage } from 'gatsby-plugin-image';
import GEJP from '../../components/StudyDesign/__study-design/gejp';
import styled from 'styled-components';

const ContentWrapper = styled.div`
margin-top: -10px;
`;

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-811 - Clinical Findings & Study Design`,
    keywords: `keywords`,
    description: `Health care professionals may find clinical findings and study design from the KEYNOTE-811 clinical trial for patients with advanced HER2-positive gastric or GEJ adenocarcinoma whose tumors express PD-L1.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with trastuzumab, fluoropyrimidine- and platinum-containing chemotherapy, for the first-line treatment of adults with locally advanced unresectable or metastatic HER2-positive gastric or gastroesophageal junction (GEJ) adenocarcinoma whose tumors express PD-L1 (CPS ≥1) as determined by an FDA-authorized test."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"MedicalWebPage","name":"Advanced HER2-Positive Gastric or GEJ Adenocarcinoma","description":"First-Line Combination Therapy for Advanced HER2-Positive Gastric or GEJ Adenocarcinoma for Tumors Expressing PD-L1 (CPS ≥1)"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"MedicalTrial","name":"Clinical Findings From KEYNOTE⁠-⁠811","description":"The efficacy of KEYTRUDA in combination with trastuzumab plus fluoropyrimidine and platinum chemotherapy was investigated in KEYNOTE-811, Phase 3, multicenter, randomized, double-blind, placebo-controlled trial that enrolled 698 patients with HER2-positive advanced gastric or gastroesophageal junction (GEJ) adenocarcinoma who had not previously received systemic therapy for metastatic disease. ","trialDesign":"RandomizedTrial"}`
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION NUMBER
const indicationId = 39; 
const jobCode = jobCodes[40].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Clinical Data for KEYTRUDA",
            items: [
                { text: '1L Combo Therapy-Advanced Esophageal or GEJ Carcinoma ', url: '/efficacy/esophageal-carcinoma/' },
                { text: '1L Combo Therapy-Advanced HER2-Negative Gastric or GEJ Adenocarcinoma ', url: '/efficacy/gastric-adenocarcinoma/' },
                { text: 'Upper GI NCCN Recommendations', url: '/static/pdf/Upper-GI-NCCN.pdf', target: '_blank' },
                    ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/her2-positive-gastric-adenocarcinoma-keynote-811/' },
                { text: 'Dosing', url: '/dosing/options/#her2-p' },
                    ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [{
        label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;811 for KEYTRUDA',
    },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;811 for KEYTRUDA' }
];

const KM811OsFootnotes = [
    {
        label: "a.",
        text: "Based on the unstratified Cox proportional hazard model."
    },
    {
        label: "b.",
        text: "Investigator's choice of 5-FU plus cisplatin (FP) or oxaliplatin plus capecitabine (CAPOX)."
    },
    {
        label: "c.",
        text: "Based on Kaplan-Meier estimation."
    },
]

const gastricFootnoteCPS1 = [
    ...KM811OsFootnotes,
    {
        label: "d.",
        text: "Best objective response as confirmed complete response or partial response."
    },
    // {
    //     label: "d.",
    //     text: "+ Denotes ongoing response."
    // },
]


const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location } >
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" isBranding/>
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                        <AnchorLinks items={anchorLinkModelData} />
                   </PageSection>
                   <PageSection bgColor="gradient">
                        <PrimaryClaim theme='light'>
                            Clinically Meaningful Improvement in OS for 1L Treatment With KEYTRUDA + Trastuzumab + Chemotherapy vs Trastuzumab + Chemotherapy Alone in Advanced HER2-Positive Gastric or GEJ Adenocarcinoma (PD-L1 Expressors)
                        </PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        
                        <PageSection title={anchorLinkModelData[0].label} bgColor="cloud" >
                            <PageSubSection>
                                    <Heading.H4 as='H3' dangerouslySetInnerHTML={createMarkup('Patients with PD-L1 CPS ≥1 (n=594)')} />
                                <SecondaryCallout title = '21% reduction in the risk of death with KEYTRUDA + trastuzumab + chemotherapy vs trastuzumab + chemotherapy alone' />
                                <KMCurve
                                    title="Kaplan-Meier curve for OS in patients with PD-L1 CPS ≥1 "
                                    hr="HR<sup>a</sup> = 0.79 (95% CI, 0.66–0.95)"
                                    image={() => <StaticImage 
                                        src="../../assets/KN811_CPS_1_KM_Curve.png"
                                        placeholder="none"
                                        alt="Kaplan-Meier Curve for OS in Patients With PD-L1 CPS ≥1 From KEYNOTE-811" />
                                    }
                                    additionalInformation={[
                                        {
                                            text:`<p>Median OS in months<sup>c</sup> (95% CI): 20.1 (17.9–22.9) with KEYTRUDA + trastuzumab + chemo<sup>b</sup> vs 15.7 (13.5–18.5) with trastuzumab + chemo<sup>b</sup> alone (HR<sup>a</sup>=0.79; 95% CI, 0.66–0.95).</p>
                                            <p>Events observed: 76% (226/298) with KEYTRUDA + trastuzumab + chemo<sup>b</sup> vs 82% (244/296) with trastuzumab + chemo<sup>b</sup> alone.</p>`
                                        },]}
                                    footnotes = {KM811OsFootnotes}
                                />
                                <ContentWrapper>
                                    <TextBlock definitions="1L = first line; 5-FU = 5-fluorouracil; CI = confidence interval; CPS = combined positive score; GEJ = gastroesophageal junction; HER2 = human epidermal growth factor receptor 2; HR = hazard ratio; ITT = intention to treat; OS = overall survival; PD⁠-⁠L1 = programmed death ligand 1.">
                                            <p><strong>ITT (N=698):</strong> A statistically significant improvement in OS and PFS was demonstrated in patients randomized to KEYTRUDA in combination with trastuzumab and chemotherapy compared with placebo in combination with trastuzumab and chemotherapy.</p>
                                            <p><strong>CPS &lt;1 (n=104):</strong> An exploratory analysis of OS in the PD-L1 CPS &lt;1 population showed a HR of 1.10 (95% CI: 0.72, 1.68), indicating that the improvement in the ITT population was primarily attributed to the results observed in the subgroup of patients with PD-L1 CPS ≥1.</p>
                                    </TextBlock>
                                </ContentWrapper>
                            </PageSubSection>
                            <PageSubSection title='Progression-Free Survival (PFS) in Patients with PD-L1 CPS ≥1'>
                                <ComparisonTable  
                                // title= 'Progression-Free Survival in Patients with CPS ≥1'                            
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA + trastuzumab + chemotherapy<sup>b</sup> (n=298)',
                                            },
                                            {
                                                legendTitle: 'Trastuzumab + chemotherapy<sup>b</sup> alone (n=296)',
                                            },
                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                hrCiP: 'HR<sup>a</sup>=0.72 (95% CI, 0.60–0.87)',
                                                centerText: true
                                            },
                                        ],                                      
                                        [
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '221 (74%)',
                                            },
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '226 (76%)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median in months<sup>c</sup> (95% CI) ',
                                                dataPoint: '10.9 (8.5–12.5)'
                                            },
                                            {
                                                label: 'Median in months<sup>c</sup> (95% CI) ',
                                                dataPoint: '7.3 (6.8–8.4)'
                                            },
                                        ],                                       
                                    ]
                                
                                } 
                            />
                            <TextBlock footnotes={gastricFootnoteCPS1} definitions="+Denotes ongoing response <p>5-FU = 5-fluorouracil; CI = confidence interval; CPS = combined positive score; DOR = Duration of Response; HR = hazard ratio; ORR = objective response rate; PFS = progression-free survival.</p>" >
                                <h4>Objective Response Rate<sup>d</sup> and Duration of Response in Patients with CPS ≥1:</h4>
                                <p>ORR<sup>d</sup> (95% CI) was 73% (68–78) with KEYTRUDA + trastuzumab + chemo<sup>b</sup> vs 58% (53–64) with trastuzumab + chemo alone. There were 17% complete responses with KEYTRUDA + trastuzumab + chemo vs 10% complete responses with trastuzumab + chemo alone and 56% partial responses with KEYTRUDA + trastuzumab + chemo vs 48% partial responses with trastuzumab + chemo alone. The median in months<sup>c</sup> (95% CI) for DOR was 11.3 (9.9–13.7) months with KEYTRUDA + trastuzumab + chemo vs 9.6 (7.1–11.2) months with trastuzumab + chemo alone and range in months was 1.1+ to 60.8+ with KEYTRUDA + trastuzumab + chemo vs 1.4+ to 60.5+ with trastuzumab + chemo alone.</p>
                            </TextBlock>

                            </PageSubSection>
                        </PageSection>
                        <PageSection bgColor='white' title={anchorLinkModelData[1].label}>
                            <PageSubSection>
                              <GEJP />
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>
                
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
